import dayjs, { type Dayjs } from "dayjs"
import type { CalendarEvent } from "@ilamy/calendar"
import type { BusinessHours, CalendarBooking } from "@/types"

const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const

export const PAUSA_COLOR = "#9ca3af"

export function envelopeBusinessHours(
  hours: BusinessHours[] | null | undefined,
): BusinessHours[] | undefined {
  if (!hours?.length) return undefined

  const perDay = new Map<string, { startTime: number; endTime: number }>()
  for (const rule of hours) {
    for (const day of rule.daysOfWeek) {
      const current = perDay.get(day)
      if (!current) {
        perDay.set(day, {
          startTime: rule.startTime,
          endTime: rule.endTime,
        })
        continue
      }
      perDay.set(day, {
        startTime: Math.min(current.startTime, rule.startTime),
        endTime: Math.max(current.endTime, rule.endTime),
      })
    }
  }

  const grouped = new Map<string, BusinessHours>()
  for (const [day, range] of perDay) {
    const key = `${range.startTime}-${range.endTime}`
    const existing = grouped.get(key)
    if (existing) {
      existing.daysOfWeek.push(day)
    } else {
      grouped.set(key, {
        daysOfWeek: [day],
        startTime: range.startTime,
        endTime: range.endTime,
      })
    }
  }

  return Array.from(grouped.values())
}

export function buildPausaEvents(
  hours: BusinessHours[] | null | undefined,
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
): CalendarEvent[] {
  if (!hours?.length) return []

  const windowsByDay = new Map<number, { start: number; end: number }[]>()
  for (const rule of hours) {
    for (const day of rule.daysOfWeek) {
      const weekday = WEEKDAYS.indexOf(day as (typeof WEEKDAYS)[number])
      if (weekday < 0) continue
      const list = windowsByDay.get(weekday) ?? []
      list.push({ start: rule.startTime, end: rule.endTime })
      windowsByDay.set(weekday, list)
    }
  }

  const events: CalendarEvent[] = []
  let cursor = rangeStart.startOf("day")
  const last = rangeEnd.startOf("day")

  while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
    const windows = (windowsByDay.get(cursor.day()) ?? [])
      .slice()
      .sort((a, b) => a.start - b.start)

    for (let i = 0; i < windows.length - 1; i += 1) {
      const gapStart = windows[i].end
      const gapEnd = windows[i + 1].start
      if (gapEnd <= gapStart) continue

      const start = applyHourValue(cursor, gapStart)
      const end = applyHourValue(cursor, gapEnd)
      const id = `pausa-${cursor.format("YYYY-MM-DD")}-${gapStart}`
      const data: CalendarBooking = {
        id,
        title: "Pausa",
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: PAUSA_COLOR,
        color: "#ffffff",
        client_id: null,
        client_name: null,
        service_id: null,
        service_name: "Pausa",
        service_emoji: null,
        staff_assignment_id: null,
        staff_name: null,
        space_id: null,
        space_name: null,
        status: "busy",
        notes: "La sucursal no atiende en este horario.",
        event_type: "pausa",
        editable: false,
      }

      events.push({
        id,
        title: "Pausa",
        start,
        end,
        backgroundColor: PAUSA_COLOR,
        color: "#ffffff",
        data,
      })
    }

    cursor = cursor.add(1, "day")
  }

  return events
}

function applyHourValue(day: Dayjs, value: number) {
  const hour = Math.floor(value)
  const minute = Math.round((value - hour) * 60)
  return day.hour(hour).minute(minute).second(0).millisecond(0)
}
